import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';

// Lowrider — passes down the street, hydraulics bouncing to the track's beat.
// The "world listens to the music" (v0.2 §5): bounce phase locks to modulation.bpm.
export default function Lowrider({ beatPhase = 0, energy = 0.5, ...props }) {
  const body = useRef();
  const group = useRef();
  const carScale = useWorldStore((s) => s.carScale); // town-scale fit (?cars=edit)

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    // Cruise slowly down the street (looping).
    if (group.current) {
      const speed = 2.2; // m/s
      const street = 140;
      const x = ((t * speed) % street) - street / 2;
      group.current.position.x = -x; // right-to-left pass
    }
    // Hydraulics: front/rear bounce on beat.
    if (body.current) {
      const bounce = Math.abs(Math.sin(beatPhase * Math.PI * 2));
      const e = 0.25 + energy * 0.75;
      body.current.position.y = 0.55 + bounce * 0.12 * e;
      body.current.rotation.z = Math.sin(beatPhase * Math.PI * 2) * 0.02 * e;
    }
  });

  // HERO_FIT: the lowrider body (~4.7 units) is larger than the route traffic
  // cars (~3.8), so at an equal carScale it would read bigger — this extra
  // reduction keeps the hero car in scale with the traffic under one knob.
  const HERO_FIT = 0.25;
  return (
    <group ref={group} position={[0, 0, -6]} {...props}>
     <group scale={carScale * HERO_FIT}>
      <group ref={body} position={[0, 0.55, 0]}>
        {/* Body — clearcoat candy paint (car-example look); reflects the
            procedural Environment set in the town scene. */}
        <mesh castShadow>
          <boxGeometry args={[4.6, 0.7, 1.9]} />
          <meshPhysicalMaterial color="#7a1fa2" metalness={0.6} roughness={0.28} clearcoat={1} clearcoatRoughness={0.08} envMapIntensity={1.4} />
        </mesh>
        {/* Cabin — tinted glass */}
        <mesh castShadow position={[-0.2, 0.6, 0]}>
          <boxGeometry args={[2.2, 0.65, 1.7]} />
          <meshPhysicalMaterial color="#2a2a35" metalness={0.9} roughness={0.08} clearcoat={1} clearcoatRoughness={0.05} envMapIntensity={1.6} />
        </mesh>
        {/* Chrome strip */}
        <mesh position={[0, -0.05, 0]}>
          <boxGeometry args={[4.7, 0.08, 1.95]} />
          <meshStandardMaterial color="#d9d9e0" metalness={1} roughness={0.05} />
        </mesh>
      </group>
      {/* Wheels */}
      {[[-1.6, 0.95], [1.6, 0.95], [-1.6, -0.95], [1.6, -0.95]].map(([x, z], i) => (
        <mesh key={i} castShadow position={[x, 0.35, z]} rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.35, 0.35, 0.25, 20]} />
          <meshStandardMaterial color="#111" metalness={0.6} roughness={0.4} />
        </mesh>
      ))}
     </group>
    </group>
  );
}